/* scai.fm's homepage (docs/next-chapter/design/mockups/homepage.html): a tuner dial with the stations as preset cards
   that play right here, one faceplate each for Zones, Tape and Studio, the Noodle Squad, and the footer. The full
   receiver (meters, the DJ feed, schedule, playlist) is at /s/<station>. Listening here counts as a listener, as the
   receiver does. */
import { For, Show, createMemo, createSignal, createEffect, onCleanup, onMount } from 'solid-js';
import { Faceplate, Glass, Key, Lamp, Readout, Sym } from '../../hifi';
import { createStreamPlayer } from '../../audio/player';
import { API, ApiError, RadioItem, RadioStation, api } from '../../api';

const LOW = 88;
const HIGH = 108;
const PLAYER_ID = Array.from(crypto.getRandomValues(new Uint8Array(12)), (b) => b.toString(16).padStart(2, '0')).join('');
const upper = (s: string) => s.toUpperCase();
const firstSentence = (s: string) => (s.match(/^.*?[.!?](\s|$)/)?.[0] ?? s).trim();

const ZONES = 'https://zones.scai.fm';
const TAPE = 'https://tape.scai.fm';
const STUDIO = 'https://studio.scai.fm';

type ProductKey = 'zones' | 'tape' | 'studio';
const PRODUCTS: [ProductKey, string][] = [['zones', 'Zones'], ['tape', 'Tape'], ['studio', 'Studio']];
// a product panel's "Request an invitation" ticks that product in the form below
const [wanted, setWanted] = createSignal<ProductKey[]>([]);
const box = 'w-full min-w-0 rounded-[3px] border border-edge bg-glass px-4 font-mono text-[15px] text-legend';
const field = `h-[46px] ${box}`;

function LinkKey(props: { href: string; legend: string; primary?: boolean; onClick?: () => void }) {
  return (
    <a href={props.href} onClick={() => props.onClick?.()}
      class={`key ${props.primary ? 'primary' : ''} inline-flex h-10 items-center px-4 no-underline`}>
      <span class="legend" style={{ color: 'inherit' }}>{props.legend}</span>
    </a>
  );
}

function Product(props: { name: string; product: ProductKey; lamp?: { tone: 'green' | 'warn'; word: string }; lit: string;
                          muted: string; text: string; keys: { href: string; legend: string }[] }) {
  return (
    <Faceplate as="section" class="flex flex-col gap-4 p-6" label={props.name}>
      <div class="flex items-center gap-3">
        <h2 class="wordmark text-[26px] font-semibold text-legend">scai.fm <span class="text-vfd">{props.name}</span></h2>
        <Show when={props.lamp}><span class="ml-auto"><Lamp tone={props.lamp!.tone} word={props.lamp!.word} /></span></Show>
      </div>
      <Glass class="flex flex-col gap-1 px-4 py-3">
        <span class="vfd text-[15px] leading-snug tracking-wider">{props.lit}</span>
        <span class="vfd-muted text-[13px] leading-snug tracking-wider">{props.muted}</span>
      </Glass>
      <p class="flex-1 text-[15px] leading-relaxed text-legend">{props.text}</p>
      <div class="flex flex-wrap gap-3">
        <For each={props.keys}>{(k, i) => <LinkKey href={k.href} legend={k.legend} primary={i() === 0} />}</For>
        <LinkKey href="#invite" legend="Request an invitation"
          onClick={() => setWanted([...new Set([...wanted(), props.product])])} />
      </div>
    </Faceplate>
  );
}

export default function Home() {
  const [stations, setStations] = createSignal<RadioStation[]>([]);
  const [skew, setSkew] = createSignal(0);
  const [clock, setClock] = createSignal(Date.now());
  const [playing, setPlaying] = createSignal<string | null>(null); // the station started from a card
  const player = createStreamPlayer();
  const timers: number[] = [];

  const listed = createMemo(() => stations().filter((s) => s.preset !== null).sort((a, b) => a.preset! - b.preset!));
  const onAir = () => listed().filter((s) => s.on_air).length;
  const tuned = createMemo(() => listed().find((s) => s.slug === playing()) ?? listed()[0] ?? null);
  const squad = createMemo(() => {
    const seen = new Map<string, string>();
    for (const s of listed()) if (s.dj && !seen.has(s.dj.name)) seen.set(s.dj.name, s.dj.bio ? firstSentence(s.dj.bio) : '');
    return [...seen];
  });

  async function load() {
    try {
      const r = await api<{ server_time: string; stations: RadioStation[] }>('/public/stations');
      setStations(r.stations);
      setSkew(Date.parse(r.server_time) - Date.now());
    } catch { /* keep what we have; the next load tries again */ }
  }

  // a card stays pressed from Listen to Stop, also while the player retries a stream that dropped
  const live = (s: string | null) => s !== null && s === playing() && player.state() !== 'idle';
  function listen(st: RadioStation) {
    if (live(st.slug)) { player.stop(); setPlaying(null); return; }
    player.stop();
    setPlaying(st.slug);
    player.setUrls(st.stream.hls, st.stream.mp3);
    player.setVolume(0.8);
    player.start();
  }

  // while a card plays, this page counts as a listener of that station
  createEffect(() => {
    const slug = playing();
    if (!slug || player.state() !== 'playing') return;
    const beat = (on: boolean) => fetch(`${API}/public/stations/${slug}/listening`, { method: 'POST',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ player: PLAYER_ID, playing: on }) }).catch(() => {});
    void beat(true);
    const t = window.setInterval(() => void beat(true), 30_000);
    onCleanup(() => { clearInterval(t); void beat(false); });
  });

  const current = createMemo<RadioItem | null>(() => {
    const t = player.audibleMs() ?? clock() + skew();
    let best: RadioItem | null = null;
    for (const i of tuned()?.items ?? []) if (Date.parse(i.started_at) <= t) best = i;
    return best;
  });
  const trackLine = () => {
    const st = tuned();
    if (!st) return 'TUNING';
    if (!st.on_air) return 'OFF AIR';
    if (player.state() === 'blocked' && playing()) return 'PRESS ▶ TO LISTEN';
    if (player.state() === 'offline' && playing()) return 'NO SIGNAL';
    const c = current();
    if (c?.kind === 'link') return upper(`${st.dj?.name ?? 'The DJ'} on the mic`);
    if (c?.track) return upper(`${c.track.title} — ${c.track.artist}`);
    return upper(st.name);
  };
  // the needle stands at the tuned preset's place on the dial
  const at = (i: number) => (i + 0.5) / Math.max(listed().length, 1);
  const needle = () => at(Math.max(listed().indexOf(tuned()!), 0));

  onMount(async () => {
    document.title = 'scai.fm · Radio made by AI DJs';
    await load();
    timers.push(window.setInterval(load, 20_000));
    timers.push(window.setInterval(() => setClock(Date.now()), 1000));
  });
  onCleanup(() => { timers.forEach(clearInterval); player.stop(); });

  return (
    <div class="mx-auto flex min-h-screen w-full max-w-[1200px] flex-col gap-[14px] p-3 sm:p-6">
      <Faceplate as="header" class="flex flex-wrap items-center gap-x-6 gap-y-3 px-5 py-3 sm:px-6">
        <a href="/" class="flex items-center gap-3 no-underline">
          <img src="/mark.png" alt="" class="h-8 w-11 object-contain" />
          <span class="wordmark text-2xl font-semibold text-legend">scai.fm</span>
        </a>
        <span class="flex items-center rounded-[3px] border border-hairline px-2.5 py-1">
          <Lamp tone={onAir() ? 'red' : 'off'} word={`${onAir()} ${onAir() === 1 ? 'stream' : 'streams'} on air`} /></span>
        <nav class="flex flex-wrap gap-2 sm:ml-auto" aria-label="Sections">
          <For each={[['Radio', tuned() ? `/s/${tuned()!.slug}` : '#radio'], ['Zones', '#zones'], ['Tape', '#tape'],
                      ['Studio', '#studio'], ['The DJs', '#djs']]}>
            {([w, href]) => <a href={href} class="key inline-flex h-9 items-center px-3.5 no-underline">
              <span class="legend" style={{ color: 'inherit' }}>{w}</span></a>}
          </For>
        </nav>
      </Faceplate>

      <Faceplate as="main" class="flex flex-col gap-5 p-5 sm:p-6" label="Radio">
        <div id="radio" class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <h1 class="wordmark max-w-[760px] text-[34px] font-semibold leading-[1.05] text-legend sm:text-[46px]">
            Radio made by AI DJs. Everyone hears the same thing at the same time.</h1>
          <p class="max-w-[360px] text-[15px] leading-relaxed text-legend-muted">
            Everything on scai.fm is AI-generated: the music, the voices, the DJs. Pick a preset and listen. No account,
            no algorithm, no skipping.</p>
        </div>

        <Glass class="flex flex-col gap-2 px-4 pb-3 pt-2 sm:px-5" role="group" aria-label="Tuner">
          <div class="relative h-9 border-b border-hairline">
            <For each={[88, 92, 98, 104, 108]}>{(f) => (
              <span class="vfd-muted absolute top-1 -translate-x-1/2 whitespace-nowrap text-xs" style={{ left: `${((f - LOW) / (HIGH - LOW)) * 92 + 4}%` }}>
                {f === HIGH ? `${f} FM` : f}</span>
            )}</For>
            <span class="absolute bottom-0 top-0 w-[2px] bg-[var(--lamp-red)] shadow-[var(--lamp-red-glow)] transition-[left] duration-300"
              style={{ left: `${needle() * 92 + 4}%` }} aria-hidden="true" />
          </div>
          <div class="grid grid-cols-1 gap-1 sm:grid-cols-[1fr_1.4fr_auto] sm:items-center sm:gap-4">
            <Readout size="m" text={tuned() ? upper(`${tuned()!.name} · ${tuned()!.programme.name}`) : ' '} />
            <Readout size="m" text={trackLine()} live />
            <Readout size="m" tone={tuned()?.on_air ? 'red' : 'muted'} text={tuned()?.on_air ? 'ON AIR' : 'OFF AIR'} />
          </div>
        </Glass>

        <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          <For each={listed()}>{(st) => (
            <div class="key flex cursor-default flex-col gap-2 p-4 text-left"
              aria-pressed={live(st.slug)} role="group" aria-label={st.name}>
              <Lamp tone={live(st.slug) ? 'red' : st.on_air ? 'green' : 'off'}
                word={`Preset ${st.preset} · ${live(st.slug) ? 'listening' : st.on_air ? 'on air' : 'off air'}`} />
              <span class="wordmark text-[22px] font-semibold leading-tight text-legend">{st.name}</span>
              <span class="flex-1 text-sm leading-snug text-legend-muted">
                {st.dj ? `${st.dj.name} · ` : ''}{st.programme.name}
                {st.on_air ? ` · ${st.listeners.toLocaleString('en')} listening` : ''}</span>
              <div class="flex items-center justify-between gap-2">
                <button class={`flex items-center gap-1.5 border-0 bg-transparent p-0 ${live(st.slug) ? 'text-vfd' : st.on_air ? 'cursor-pointer text-legend' : 'text-legend-muted'}`}
                  disabled={!st.on_air} onClick={() => listen(st)}>
                  <Sym name={live(st.slug) ? 'stop' : 'play'} />
                  <span class="legend" style={{ color: 'inherit' }}>{live(st.slug) ? 'Stop' : 'Listen'}</span>
                </button>
                <a href={`/s/${st.slug}`} class="legend no-underline hover:text-legend">Receiver →</a>
              </div>
            </div>
          )}</For>
          <div class="flex flex-col gap-2 rounded-[var(--radius-button)] border border-dashed border-hairline p-4">
            <Lamp tone="off" word={`Preset ${listed().length + 1} · soon`} />
            <span class="wordmark text-[22px] font-semibold leading-tight text-legend-muted">Dutch, German, French</span>
            <span class="text-sm leading-snug text-legend-muted">Streams in your language follow once the English ones have settled in.</span>
          </div>
        </div>
      </Faceplate>

      <div class="grid gap-[14px] lg:grid-cols-3">
        <div id="zones" class="flex scroll-mt-4 flex-col [&>*]:flex-1"><Product name="Zones" product="zones" lit="SHOP FLOOR · POP 50 · INDIE 30 · ACOUSTIC 20"
          muted="NO BUMA/STEMRA · NO SENA · YOUR ZONES, YOUR MIX"
          text="License-free music for your business. One zone per room, a genre mix per zone, playlists that build themselves, any streaming device or a tablet on the counter."
          keys={[{ href: ZONES, legend: '▶ Sign in to Zones' }]} /></div>
        <div id="tape" class="flex scroll-mt-4 flex-col [&>*]:flex-1"><Product name="Tape" product="tape" lamp={{ tone: 'warn', word: 'Beta' }}
          lit="WRITE · MAKE · KEEP" muted="A SONG IN A MINUTE · EVERY TAKE A VERSION"
          text="Make songs from lyrics, yours or Poolnoodle's: a finished song with vocals in about a minute, every take kept as a version, MP3 or FLAC to take with you. English, and Dutch to try. By invitation while in beta."
          keys={[{ href: TAPE, legend: '● Sign in to Tape' }]} /></div>
        <div id="studio" class="flex scroll-mt-4 flex-col [&>*]:flex-1"><Product name="Studio" product="studio" lamp={{ tone: 'green', word: 'Open' }}
          lit="YOUR STATION · ON AIR" muted="OUR LIBRARY · YOUR SONGS · YOUR DJ · YOUR IDENTS"
          text="Your own virtual radio station, hosted. Start from our library, upload your songs and idents, give it a Noodle Squad host or a voice of your own, and set its format and schedule."
          keys={[{ href: STUDIO, legend: '▶ Sign in to Studio' }]} /></div>
      </div>

      <Invite />

      <Show when={squad().length}>
        <Faceplate as="section" class="flex flex-col gap-3 px-6 py-5" label="The Noodle Squad">
          <span id="djs" class="legend">The Noodle Squad</span>
          <div class="flex flex-wrap gap-x-8 gap-y-2 text-[15px] text-legend">
            <For each={squad()}>{([name, line]) => (
              <span><strong class="font-semibold">{name}</strong><Show when={line}><span class="text-legend-muted"> · {line}</span></Show></span>
            )}</For>
          </div>
        </Faceplate>
      </Show>

      <footer class="flex flex-wrap items-center justify-between gap-2 px-1 pb-4 text-sm text-legend-muted">
        <span>Everything on scai.fm is AI-generated. Operated by ScaiLabs B.V., Elsloo, the Netherlands.</span>
        <span class="font-mono text-xs">
          <For each={[['/', 'scai.fm'], [ZONES, 'zones.scai.fm'], [TAPE, 'tape.scai.fm'], [STUDIO, 'studio.scai.fm']]}>
            {([href, host]) => <><a href={href} class="text-legend-muted underline-offset-2 hover:text-legend hover:underline">{host}</a>{' · '}</>}
          </For>api.scai.fm</span>
      </footer>
    </div>
  );
}

/** "Request an invitation": no self sign-up yet, so people ask; scai.fm answers by mail. The hidden field is for bots. */
function Invite() {
  const [f, setF] = createSignal({ name: '', email: '', company: '', message: '', website: '' });
  const [busy, setBusy] = createSignal(false);
  const [done, setDone] = createSignal(false);
  const [err, setErr] = createSignal('');
  const set = (k: keyof ReturnType<typeof f>) => (e: { currentTarget: { value: string } }) => setF({ ...f(), [k]: e.currentTarget.value });
  const toggle = (p: ProductKey) => setWanted(wanted().includes(p) ? wanted().filter((x) => x !== p) : [...wanted(), p]);
  async function send(e: Event) {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      await api('/public/invitations', { method: 'POST', body: JSON.stringify({ ...f(), products: wanted() }) });
      setDone(true);
    } catch (x) {
      setErr(x instanceof ApiError ? x.message : 'Could not send it; try again in a minute.');
    } finally { setBusy(false); }
  }
  return (
    <div id="invite" class="scroll-mt-4">
      <Faceplate as="section" class="flex flex-col gap-4 p-6" label="Request an invitation">
        <div class="flex flex-col gap-1">
          <h2 class="wordmark text-[26px] font-semibold text-legend">Request an invitation</h2>
          <p class="text-[15px] text-legend-muted">Zones, Tape and Studio are by invitation for now. Tell us who you are and what you
            would like to do; we answer by e-mail, usually within a working day. Radio needs no account: just press Listen.</p>
        </div>
        <Show when={!done()} fallback={
          <Glass class="px-4 py-3"><span class="vfd text-[15px] tracking-wider">THANK YOU · WE'LL BE IN TOUCH BY E-MAIL</span></Glass>}>
          <form class="flex flex-col gap-3" onSubmit={send}>
            <div class="grid gap-3 sm:grid-cols-3">
              <input class={field} required maxLength={100} placeholder="Your name" aria-label="Your name" value={f().name} onInput={set('name')} />
              <input class={field} required type="email" maxLength={255} placeholder="E-mail" aria-label="E-mail" value={f().email} onInput={set('email')} />
              <input class={field} maxLength={150} placeholder="Company (optional)" aria-label="Company (optional)" value={f().company} onInput={set('company')} />
            </div>
            <div class="flex flex-wrap items-center gap-3" role="group" aria-label="What you would like">
              <span class="legend">I'd like</span>
              <For each={PRODUCTS}>{([p, name]) => (
                <button type="button" class="key h-10 px-4" aria-pressed={wanted().includes(p)} onClick={() => toggle(p)}>
                  <span class="flex items-center gap-2"><span class={`lamp ${wanted().includes(p) ? 'green' : ''}`} aria-hidden="true" />
                    <span class="legend" style={{ color: 'inherit' }}>{name}</span></span></button>
              )}</For>
            </div>
            <textarea class={`${box} h-28 py-3`} maxLength={2000} placeholder="What would you like to do with it? (optional)"
              aria-label="Message (optional)" value={f().message} onInput={set('message')} />
            {/* people never see or fill this; bots that fill every field are kept as spam */}
            <input class="absolute -left-[9999px] h-px w-px opacity-0" tabIndex={-1} autocomplete="off" aria-hidden="true"
              name="website" value={f().website} onInput={set('website')} />
            <div class="flex flex-wrap items-center gap-4">
              <button type="submit" class="key primary h-12 px-5" disabled={busy() || !wanted().length}>
                <span class="legend" style={{ color: 'inherit' }}>{busy() ? 'Sending…' : 'Send request'}</span></button>
              <span class="text-sm" role="status" style={{ color: err() ? 'var(--lamp-warn)' : 'var(--legend-muted)' }}>
                {err() || (wanted().length ? 'Poolnoodle reads it first, to keep the spam out.' : 'Pick at least one product.')}</span>
            </div>
          </form>
        </Show>
      </Faceplate>
    </div>
  );
}
